// Export utilities for PDF and Excel
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to format time in 12-hour format with AM/PM
const formatTime12Hour = (time) => {
    if (!time) return '-';
    try {
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
        return time;
    }
};

// Helper function to format date
const formatDate = (date) => {
    if (!date) return '-';
    try {
        const dateStr = date.split('T')[0];
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    } catch (e) {
        return date;
    }
};

// Generate PDF blob for preview
const generatePDFBlob = (doc) => {
    return doc.output('blob');
};

// Show PDF preview in modal
let pdfPreviewCallback = null;
window.showPDFPreview = (blob, filename) => {
    if (pdfPreviewCallback) {
        pdfPreviewCallback(blob, filename);
    }
};

export const setPDFPreviewCallback = (callback) => {
    pdfPreviewCallback = callback;
};

// Export to Excel (CSV format)
export const exportToExcel = (data, filename, columns) => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    const headers = columns.map(col => col.label).join(',');
    const rows = data.map(item => 
        columns.map(col => {
            const value = item[col.key];
            // Escape quotes and wrap in quotes if contains comma
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value ?? '';
        }).join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Export to PDF (basic)
export const exportToPDF = (data, filename, columns, title) => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text(title || filename, 14, 22);
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
    
    // Prepare table data
    const tableHeaders = columns.map(col => col.label);
    const tableData = data.map(item => 
        columns.map(col => {
            const value = item[col.key];
            return value ?? '-';
        })
    );
    
    // Generate table
    autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [79, 70, 229] }, // Indigo color
    });
    
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
};

// Custom PDF Export for Attendance
export const exportAttendancePDF = (attendances, employees, title = 'Attendance Report', options = {}) => {
    const { preview = false, onPreview = null, onDownload = null } = options;
    
    if (!attendances || attendances.length === 0) {
        alert('No data to export');
        return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header with company name
    doc.setFillColor(79, 70, 229); // Indigo
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('HRM System', 14, 18);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Employee Attendance Report', 14, 25);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Report info
    doc.setFontSize(10);
    doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 40);
    doc.text(`Total Records: ${attendances.length}`, 14, 46);
    
    // Summary statistics
    const presentCount = attendances.filter(a => a.status === 'present').length;
    const absentCount = attendances.filter(a => a.status === 'absent').length;
    const lateCount = attendances.filter(a => a.status === 'late').length;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary:', 14, 56);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Present: ${presentCount}`, 14, 62);
    doc.text(`Absent: ${absentCount}`, 60, 62);
    doc.text(`Late: ${lateCount}`, 100, 62);
    
    // Prepare table data
    const tableData = attendances.map(att => {
        const employee = employees.find(e => e.id === att.employee_id);
        return [
            employee?.name || '-',
            formatDate(att.date),
            formatTime12Hour(att.check_in),
            formatTime12Hour(att.check_out),
            att.status || '-',
            att.remarks || '-'
        ];
    });
    
    // Generate table
    autoTable(doc, {
        head: [['Employee', 'Date', 'Check In', 'Check Out', 'Status', 'Remarks']],
        body: tableData,
        startY: 70,
        styles: { 
            fontSize: 9,
            cellPadding: 3,
            valign: 'middle'
        },
        headStyles: { 
            fillColor: [79, 70, 229],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center'
        },
        bodyStyles: {
            halign: 'left'
        },
        alternateRowStyles: {
            fillColor: [245, 247, 250]
        },
        columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 25 },
            2: { cellWidth: 30 },
            3: { cellWidth: 30 },
            4: { cellWidth: 20 },
            5: { cellWidth: 'auto' }
        }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }
    
    const filename = `attendance_report_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (preview && onPreview) {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        onPreview(url, filename);
    } else {
        doc.save(filename);
        if (onDownload) onDownload();
    }
};

// Custom PDF Export for Movements
export const exportMovementsPDF = (movements, employees, title = 'Movement Report', options = {}) => {
    const { preview = false, onPreview = null, onDownload = null } = options;
    
    if (!movements || movements.length === 0) {
        alert('No data to export');
        return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header with company name
    doc.setFillColor(34, 197, 94); // Green for movements
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('HRM System', 14, 18);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Employee Movement Report', 14, 25);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Report info
    doc.setFontSize(10);
    doc.text(`Generated Date: ${new Date().toLocaleDateString()}`, 14, 40);
    doc.text(`Total Records: ${movements.length}`, 14, 46);
    
    // Summary by date
    const dateCounts = {};
    movements.forEach(mov => {
        const date = mov.date ? mov.date.split('T')[0] : 'Unknown';
        dateCounts[date] = (dateCounts[date] || 0) + 1;
    });
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Movement Summary by Date:', 14, 56);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let yPos = 62;
    Object.entries(dateCounts).slice(0, 5).forEach(([date, count]) => {
        doc.text(`${formatDate(date)}: ${count} movements`, 14, yPos);
        yPos += 5;
    });
    
    // Prepare table data
    const tableData = movements.map(mov => {
        const employee = employees.find(e => e.id === mov.employee_id);
        return [
            employee?.name || '-',
            formatDate(mov.date),
            mov.description || '-',
            mov.purpose || '-'
        ];
    });
    
    // Generate table
    autoTable(doc, {
        head: [['Employee', 'Date', 'Description', 'Purpose']],
        body: tableData,
        startY: yPos + 5,
        styles: { 
            fontSize: 9,
            cellPadding: 3,
            valign: 'middle'
        },
        headStyles: { 
            fillColor: [34, 197, 94],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center'
        },
        bodyStyles: {
            halign: 'left'
        },
        alternateRowStyles: {
            fillColor: [240, 255, 244]
        },
        columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 35 },
            2: { cellWidth: 'auto' },
            3: { cellWidth: 'auto' }
        }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }
    
    const filename = `movement_report_${new Date().toISOString().split('T')[0]}.pdf`;
    
    if (preview && onPreview) {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        onPreview(url, filename);
    } else {
        doc.save(filename);
        if (onDownload) onDownload();
    }
};

// Common column definitions for reuse
export const commonColumns = {
    name: { key: 'name', label: 'Name' },
    code: { key: 'code', label: 'Code' },
    email: { key: 'email', label: 'Email' },
    phone: { key: 'phone', label: 'Phone' },
    status: { key: 'status', label: 'Status' },
    createdAt: { key: 'created_at', label: 'Created At' },
};

export default { exportToExcel, exportToPDF, exportAttendancePDF, exportMovementsPDF, commonColumns };
